// FEATURE-AUTOBALANCE - typed inputs, proportional rebalance, locks, presets, decimals, range 10-35
import { useEffect, useState } from 'react';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

export default function CommissionSettings() {
  const { role } = useAuth();
  const [poolRate, setPoolRate] = useState(25);
  const [spShare, setSpShare] = useState(60);
  const [amShare, setAmShare] = useState(20);
  const [rmShare, setRmShare] = useState(20);
  const [lockSp, setLockSp] = useState(false);
  const [lockAm, setLockAm] = useState(false);
  const [lockRm, setLockRm] = useState(false);
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Track which field is being edited so input shows raw text (allows "12." while typing)
  const [editing, setEditing] = useState(null); // 'sp' | 'am' | 'rm' | 'pool' | null
  const [editingValue, setEditingValue] = useState('');

  useEffect(() => {
    client.get('/admin/commission-settings').then(r => {
      const d = r.data;
      setPoolRate(d.pool_rate != null ? parseFloat(d.pool_rate) : 25);
      setSpShare(d.sales_partner_share != null ? parseFloat(d.sales_partner_share) : 60);
      setAmShare(d.area_manager_share != null ? parseFloat(d.area_manager_share) : 20);
      setRmShare(d.regional_manager_share != null ? parseFloat(d.regional_manager_share) : 20);
    }).finally(() => setLoading(false));
  }, []);

  const fmt = (n) => {
    // Display with up to 2 decimals but trim trailing zeros (60 not 60.00, 33.5 not 33.50)
    const r = Math.round(n * 100) / 100;
    return r % 1 === 0 ? String(r) : r.toFixed(2).replace(/0$/,'').replace(/\.$/,'');
  };

  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const round2 = (n) => Math.round(n * 100) / 100;

  // Proportional rebalance — always preserves total = 100 by clamping changed value
  const rebalance = (changed, newValue) => {
    let sp = spShare, am = amShare, rm = rmShare;
    const locks = { sp: lockSp, am: lockAm, rm: lockRm };

    // Sum of LOCKED fields that aren't the changed field (these can't move)
    const lockedSum = ['sp','am','rm']
      .filter(f => f !== changed && locks[f])
      .reduce((s, f) => s + ({sp,am,rm}[f]), 0);

    // Max allowed for changed value = 100 - lockedSum
    // (since unlocked others can absorb anything from 0 to 100 - lockedSum)
    const maxAllowed = 100 - lockedSum;
    newValue = clamp(round2(newValue), 0, maxAllowed);

    if (changed === 'sp') sp = newValue;
    else if (changed === 'am') am = newValue;
    else if (changed === 'rm') rm = newValue;

    const others = ['sp','am','rm'].filter(f => f !== changed && !locks[f]);
    const remaining = 100 - newValue - lockedSum;

    if (others.length === 2) {
      const [f1, f2] = others;
      const v1 = {sp,am,rm}[f1];
      const v2 = {sp,am,rm}[f2];
      const sum = v1 + v2;
      let n1, n2;
      if (sum > 0) {
        n1 = round2(remaining * v1 / sum);
        n2 = round2(remaining - n1);
      } else {
        // Both were 0 — split equally so they can absorb future changes
        n1 = round2(remaining / 2);
        n2 = round2(remaining - n1);
      }
      n1 = clamp(n1, 0, 100);
      n2 = clamp(n2, 0, 100);
      // Correct rounding drift to guarantee total = 100
      const drift = round2(remaining - n1 - n2);
      if (Math.abs(drift) > 0.001) { n2 = round2(n2 + drift); n2 = clamp(n2, 0, 100); }
      if (f1 === 'sp') sp = n1; else if (f1 === 'am') am = n1; else rm = n1;
      if (f2 === 'sp') sp = n2; else if (f2 === 'am') am = n2; else rm = n2;
    } else if (others.length === 1) {
      const f = others[0];
      const v = clamp(round2(remaining), 0, 100);
      if (f === 'sp') sp = v; else if (f === 'am') am = v; else rm = v;
    }
    // others.length === 0 → all others locked, can't rebalance; total may not be 100 (warning shown)

    setSpShare(sp); setAmShare(am); setRmShare(rm);
  };

  // Allow free typing — commit value on blur or Enter
  const startEdit = (field, currentValue) => {
    setEditing(field);
    setEditingValue(String(currentValue));
  };
  const commitEdit = () => {
    if (editing === null) return;
    const v = parseFloat(editingValue);
    const safe = isNaN(v) ? 0 : v;
    if (editing === 'pool') {
      setPoolRate(clamp(round2(safe), 10, 35));
    } else {
      rebalance(editing, safe);
    }
    setEditing(null);
    setEditingValue('');
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.target.blur(); }
    if (e.key === 'Escape') { setEditing(null); setEditingValue(''); }
  };

  const applyPreset = (sp, am, rm) => { setSpShare(sp); setAmShare(am); setRmShare(rm); };

  const totalShares = round2(spShare + amShare + rmShare);
  const isValid = Math.abs(totalShares - 100) < 0.01;

  // Live preview math (based on $100/mo)
  const examplePlan = 100;
  const spAmt = round2(examplePlan * poolRate * spShare / 10000);
  const amAmt = round2(examplePlan * poolRate * amShare / 10000);
  const rmAmt = round2(examplePlan * poolRate * rmShare / 10000);
  const osAmt = round2(examplePlan * (100 - poolRate) / 100);
  const spOfPay = round2(poolRate * spShare / 100);
  const amOfPay = round2(poolRate * amShare / 100);
  const rmOfPay = round2(poolRate * rmShare / 100);

  const save = async () => {
    setErr(''); setMsg('');
    if (!isValid) return setErr('Shares must add up to 100%. Currently: ' + fmt(totalShares) + '%');
    if (poolRate < 10 || poolRate > 35) return setErr('Pool rate must be between 10% and 35%.');
    setSaving(true);
    try {
      await client.post('/admin/commission-settings', {
        poolRate, salesPartnerShare: spShare, areaManagerShare: amShare,
        regionalManagerShare: rmShare, note
      });
      setMsg('Commission settings updated successfully.');
      setNote('');
    } catch(e) { setErr(e.response?.data || 'Failed'); } finally { setSaving(false); }
  };

  const isAdmin = role === 'SUPER_ADMIN';
  const inp = { width:'100%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8, fontSize:14, color:'#000', outline:'none', boxSizing:'border-box' };

  if (loading) return <div style={{ color:'#888', padding:40, textAlign:'center' }}>Loading...</div>;

  // Helper: render a share card (Sales Partner / Area Manager / Regional Manager)
  const ShareCard = ({ field, value, locked, setLocked, color, lightBg, label }) => {
    const isEditingThis = editing === field;
    return (
      <div style={{ background:'#f9fafb', borderRadius:12, padding:'14px 16px', border:'1px solid #eef0f3' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:11, fontWeight:800, color, letterSpacing:'0.5px', textTransform:'uppercase' }}>{label}</div>
          <button
            onClick={() => isAdmin && setLocked(!locked)}
            disabled={!isAdmin}
            title={locked ? 'Locked — click to unlock' : 'Click to lock this value'}
            style={{
              background: locked ? color + '15' : 'transparent', border: locked ? '1px solid ' + color + '40' : '1px solid transparent', cursor: isAdmin ? 'pointer' : 'not-allowed',
              padding:'3px 8px', borderRadius:6, fontSize:12, fontWeight:600,
              color: locked ? color : '#9ca3af', transition: 'all 0.15s ease'
            }}
          >
            {locked ? '🔒 Locked' : '🔓'}
          </button>
        </div>
        <div style={{ display:'flex', alignItems:'baseline', gap:4 }}>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            inputMode="decimal"
            value={isEditingThis ? editingValue : fmt(value)}
            onFocus={() => startEdit(field, value)}
            onChange={e => {
              const v = e.target.value;
              setEditingValue(v);
              // If change came from spinner (not typing), commit immediately
              if (v !== '' && !isNaN(parseFloat(v))) {
                rebalance(field, parseFloat(v));
              }
            }}
            onBlur={commitEdit}
            onKeyDown={onKeyDown}
            disabled={!isAdmin || locked}
            style={{
              width:90, border:'1px solid #e5e7eb', background:'#fff', borderRadius:8,
              fontSize:24, fontWeight:800, color, outline:'none', padding:'8px 10px',
              cursor: (!isAdmin || locked) ? 'not-allowed' : 'text',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease'
            }}
            onFocusCapture={e => { e.target.style.borderColor = color; e.target.style.boxShadow = '0 0 0 3px ' + color + '22'; }}
            onBlurCapture={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
          />
          <span style={{ fontSize:18, fontWeight:800, color }}>%</span>
        </div>
        <div style={{ fontSize:11, color:'#888', marginTop:4 }}>of pool</div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth:760 }}>
      <h1 style={{ color:'#000', fontSize:22, fontWeight:800, margin:'0 0 4px', fontFamily:'Georgia,serif' }}>Commission Settings</h1>
      <p style={{ color:'#555', fontSize:13, margin:'0 0 28px' }}>Control how commissions are split across the sales team. Changes apply to all future payments.</p>

      {msg && <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'10px 16px', color:'#0a8a40', fontSize:13, marginBottom:16 }}>{msg}</div>}
      {err && <div style={{ background:'#fff5f5', border:'1px solid #fca5a5', borderRadius:8, padding:'10px 16px', color:'#c0392b', fontSize:13, marginBottom:16 }}>{err}</div>}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>

        {/* Pool Rate */}
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', padding:24 }}>
          <div style={{ fontSize:15, fontWeight:800, color:'#000', marginBottom:4 }}>Total Commission Pool</div>
          <div style={{ fontSize:13, color:'#666', marginBottom:20 }}>Percentage of each payment paid out as commission. Min 10%, Max 35%.</div>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
            <input type="range" min="10" max="35" step="0.5" value={poolRate}
              onChange={e => setPoolRate(parseFloat(e.target.value))}
              style={{ flex:1, accentColor:'#1a3cff' }} disabled={!isAdmin} />
            <div style={{ display:'flex', alignItems:'baseline', gap:2, minWidth:80, justifyContent:'flex-end' }}>
              <input
                type="number"
                min="10"
                max="35"
                step="0.5"
                inputMode="decimal"
                value={editing === 'pool' ? editingValue : fmt(poolRate)}
                onFocus={() => startEdit('pool', poolRate)}
                onChange={e => {
                  const v = e.target.value;
                  setEditingValue(v);
                  if (v !== '' && !isNaN(parseFloat(v))) {
                    const parsed = parseFloat(v);
                    setPoolRate(clamp(round2(parsed), 10, 35));
                  }
                }}
                onBlur={commitEdit}
                onKeyDown={onKeyDown}
                disabled={!isAdmin}
                style={{ width:80, border:'1px solid #e5e7eb', background:'#fff', borderRadius:8, fontSize:24, fontWeight:800, color:'#1a3cff', textAlign:'right', outline:'none', padding:'8px 10px', transition: 'border-color 0.15s ease, box-shadow 0.15s ease' }}
                onFocusCapture={e => { e.target.style.borderColor = '#1a3cff'; e.target.style.boxShadow = '0 0 0 3px #1a3cff22'; }}
                onBlurCapture={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
              <span style={{ fontSize:22, fontWeight:800, color:'#1a3cff' }}>%</span>
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#888' }}>
            <span>Min 10%</span><span>Max 35%</span>
          </div>
        </div>

        {/* Live Preview */}
        <div style={{ background:'#f9f9f9', borderRadius:12, border:'1px solid #eee', padding:24 }}>
          <div style={{ fontSize:15, fontWeight:800, color:'#000', marginBottom:4 }}>Live Preview</div>
          <div style={{ fontSize:13, color:'#666', marginBottom:16 }}>Based on $100/mo example</div>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'#eff4ff', borderRadius:10 }}>
              <span style={{ fontSize:13, fontWeight:600, color:'#1a3cff' }}>Sales Partner ({fmt(spOfPay)}%)</span>
              <span style={{ fontSize:14, fontWeight:800, color:'#1a3cff' }}>${fmt(spAmt)}/mo</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'#f3f0ff', borderRadius:8 }}>
              <span style={{ fontSize:13, fontWeight:600, color:'#5b21b6' }}>Area Manager ({fmt(amOfPay)}%)</span>
              <span style={{ fontSize:14, fontWeight:800, color:'#5b21b6' }}>${fmt(amAmt)}/mo</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'#fdf2f8', borderRadius:8 }}>
              <span style={{ fontSize:13, fontWeight:600, color:'#be185d' }}>Regional Manager ({fmt(rmOfPay)}%)</span>
              <span style={{ fontSize:14, fontWeight:800, color:'#be185d' }}>${fmt(rmAmt)}/mo</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'#f0fff6', borderRadius:8 }}>
              <span style={{ fontSize:13, fontWeight:600, color:'#0a8a40' }}>OurEsign keeps ({fmt(100-poolRate)}%)</span>
              <span style={{ fontSize:14, fontWeight:800, color:'#0a8a40' }}>${fmt(osAmt)}/mo</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pool Distribution */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', padding:24, marginBottom:20 }}>
        <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:4 }}>
          <div style={{ fontSize:15, fontWeight:800, color:'#000' }}>Pool Distribution</div>
          <div style={{ fontSize:11, color:'#9ca3af', fontWeight:500 }}>All three must sum to 100%</div>
        </div>
        <div style={{ fontSize:13, color:'#666', marginBottom:20 }}>Type any value. Unlocked fields rebalance automatically.</div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:14 }}>
          <ShareCard field="sp" value={spShare} locked={lockSp} setLocked={setLockSp} color="#1a3cff" label="Sales Partner" />
          <ShareCard field="am" value={amShare} locked={lockAm} setLocked={setLockAm} color="#5b21b6" label="Area Manager" />
          <ShareCard field="rm" value={rmShare} locked={lockRm} setLocked={setLockRm} color="#be185d" label="Regional Manager" />
        </div>

        {/* Visual bar */}
        <div style={{ display:'flex', height:8, borderRadius:4, overflow:'hidden', marginBottom:10, background:'#eee' }}>
          <div style={{ background:'#1a3cff', width: Math.max(0, Math.min(100, spShare)) + '%', transition:'width 0.2s' }} />
          <div style={{ background:'#5b21b6', width: Math.max(0, Math.min(100, amShare)) + '%', transition:'width 0.2s' }} />
          <div style={{ background:'#be185d', width: Math.max(0, Math.min(100, rmShare)) + '%', transition:'width 0.2s' }} />
        </div>

        <div style={{ padding:'10px 14px', background:isValid?'#f0fff6':'#fff5f5', borderRadius:8, border:'1px solid '+(isValid?'#b2dfdb':'#fca5a5'), marginBottom:14 }}>
          <span style={{ fontWeight:700, color:isValid?'#0a8a40':'#c0392b', fontSize:13 }}>
            {isValid ? '✓ Total: 100% — valid' : '✗ Total: ' + fmt(totalShares) + '% — must equal 100%'}
          </span>
        </div>

        {/* Quick presets */}
        {isAdmin && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:12, color:'#666', fontWeight:600, marginRight:4 }}>Quick presets:</span>
            <button onClick={() => applyPreset(70, 20, 10)} style={{ padding:'6px 12px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer', color:'#374151' }}>70 / 20 / 10</button>
            <button onClick={() => applyPreset(60, 20, 20)} style={{ padding:'6px 12px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer', color:'#374151' }}>60 / 20 / 20</button>
            <button onClick={() => applyPreset(50, 20, 30)} style={{ padding:'6px 12px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer', color:'#374151' }}>50 / 20 / 30</button>
            <button onClick={() => applyPreset(100, 0, 0)} style={{ padding:'6px 12px', background:'#f3f4f6', border:'1px solid #d1d5db', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer', color:'#374151' }}>100 / 0 / 0</button>
          </div>
        )}
      </div>

      {isAdmin && (
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', padding:24 }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#333', textTransform:'uppercase', display:'block', marginBottom:8 }}>Note (optional)</label>
          <input style={{ ...inp, marginBottom:16 }} placeholder="Reason for this change..." value={note} onChange={e=>setNote(e.target.value)} />
          <button
            style={{ padding:'11px 28px', background:'#1a3cff', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:(saving||!isValid)?'not-allowed':'pointer', opacity:(saving||!isValid)?0.6:1 }}
            onClick={save} disabled={saving||!isValid}>
            {saving ? '⏳ Saving...' : 'Save Commission Settings'}
          </button>
          <p style={{ color:'#888', fontSize:12, marginTop:10 }}>⚠️ Changes apply to future commission calculations only. Existing pending commissions are not affected.</p>
        </div>
      )}
    </div>
  );
}
